import { type CanonicalIcd10Code, icd10Category, parseCanonicalIcd10Code } from "@smartmed/codes";

import type { OrderedIcd10Codes } from "./examinationEncounterCodes";
import { PAIRED_CODE_RULES, type PairedCodeRule } from "./pairedCodeRules";

export type PairedCodeIssueKind = "missing-partner" | "several-partners" | "shared-partner" | "separated";

export type PairedCodeIssue = {
  kind: PairedCodeIssueKind;
  /** The review-queue flag text. Each offending code carries its own, shorter, tooltip. */
  reviewText: string;
  offendingCodes: { code: CanonicalIcd10Code; index: number; message: string }[];
};

export type PairedCodeWarning = { code: CanonicalIcd10Code; index: number; message: string };

export type PairedCodePlacement<T> = {
  codesInSequence: T[];
  placed: { partner: T; anchor: T }[];
};

type Located = { code: CanonicalIcd10Code; index: number };
type CodeGroup = { code: CanonicalIcd10Code; occurrences: Located[] };
type PresentAnchor = CodeGroup & { rule: PairedCodeRule };

const CATEGORY_LENGTH = 3;

const matches = (pattern: string, code: CanonicalIcd10Code) =>
  pattern.length === CATEGORY_LENGTH ? icd10Category(code) === pattern : code === pattern;

const matchesAny = (patterns: readonly string[], code: CanonicalIcd10Code) =>
  patterns.some((pattern) => matches(pattern, code));

const groupByCode = (located: readonly Located[]): CodeGroup[] =>
  [
    ...located.reduce(
      (groups, item) => groups.set(item.code, [...(groups.get(item.code) ?? []), item]),
      new Map<CanonicalIcd10Code, Located[]>(),
    ),
  ].map(([code, occurrences]) => ({ code, occurrences }));

const analyse = (orderedCodes: OrderedIcd10Codes, specialty: string | null | undefined) => {
  const rules = PAIRED_CODE_RULES.filter(
    (rule) => !rule.specialties || (specialty != null && rule.specialties.includes(specialty)),
  );
  const groups = groupByCode(orderedCodes.flatMap((code, index) => (code === undefined ? [] : [{ code, index }])));

  const anchors = groups.flatMap((group): PresentAnchor[] => {
    const rule = rules.find((candidate) => matchesAny(candidate.anchors, group.code));
    return rule ? [{ ...group, rule }] : [];
  });

  const partnersOf = (anchor: PresentAnchor) => groups.filter(({ code }) => matchesAny(anchor.rule.partners, code));

  // The primary can't move, so it never takes part in a pairing.
  const movablePartnersOf = (anchor: PresentAnchor) =>
    partnersOf(anchor).flatMap(({ code, occurrences }) => {
      const movable = occurrences.filter(({ index }) => index > 0);
      return movable.length > 0 ? [{ code, occurrences: movable }] : [];
    });

  const claimantsOf = (partnerCode: CanonicalIcd10Code) =>
    anchors.filter((anchor) => matchesAny(anchor.rule.partners, partnerCode));

  return { anchors, partnersOf, movablePartnersOf, claimantsOf };
};

const isDirectlyAfter = (anchorOccurrences: readonly Located[], partnerOccurrences: readonly Located[]) =>
  anchorOccurrences.some((anchor) => partnerOccurrences.some((partner) => partner.index === anchor.index + 1));

const joinAnd = (items: readonly string[]) =>
  items.length <= 2 ? items.join(" and ") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

const flagged = (occurrences: readonly Located[], message: string) =>
  occurrences.map(({ code, index }) => ({ code, index, message }));

export const findPairedCodeIssues = (
  orderedCodes: OrderedIcd10Codes,
  specialty: string | null | undefined,
): PairedCodeIssue[] => {
  const { anchors, partnersOf, movablePartnersOf, claimantsOf } = analyse(orderedCodes, specialty);

  const missing = anchors
    .filter((anchor) => anchor.rule.flagMissingPartner && partnersOf(anchor).length === 0)
    .map((anchor): PairedCodeIssue => {
      const { partnerKind, partners } = anchor.rule;
      return {
        kind: "missing-partner",
        reviewText: `${anchor.code} is expected to be paired with a ${partnerKind} (${partners.join(" or ")}) but none is present.`,
        offendingCodes: flagged(
          anchor.occurrences,
          `Expected to be paired with a ${partnerKind} (${partners.join(" or ")})`,
        ),
      };
    });

  const several = anchors.flatMap((anchor): PairedCodeIssue[] => {
    const partners = movablePartnersOf(anchor);
    const partnerOccurrences = partners.flatMap(({ occurrences }) => occurrences);
    if (partners.length < 2 || isDirectlyAfter(anchor.occurrences, partnerOccurrences)) return [];

    const count = partners.length === 2 ? "two" : "several";
    const codes = partners.map(({ code }) => code);
    const message = `${count === "two" ? "Two" : "Several"} possible paired ${anchor.rule.partnerKind}s for ${anchor.code} (${codes.join(" or ")})`;
    return [
      {
        kind: "several-partners",
        reviewText: `${anchor.code} has ${count} possible paired ${anchor.rule.partnerKind}s (${codes.join(", ")}). Place the one that applies directly after ${anchor.code}.`,
        offendingCodes: flagged([...anchor.occurrences, ...partnerOccurrences], message),
      },
    ];
  });

  const separated = anchors.flatMap((anchor): PairedCodeIssue[] => {
    const partners = movablePartnersOf(anchor);
    if (partners.length !== 1) return [];

    const [partner] = partners;
    const isSoleClaimant = claimantsOf(partner.code).length === 1;
    if (!isSoleClaimant || isDirectlyAfter(anchor.occurrences, partner.occurrences)) return [];

    return [
      {
        kind: "separated",
        reviewText: `${anchor.code} and ${partner.code} are expected to be paired. Place ${partner.code} directly after ${anchor.code}.`,
        offendingCodes: [
          ...flagged(anchor.occurrences, `Expected to be paired with ${anchor.rule.partnerKind} ${partner.code}`),
          ...flagged(partner.occurrences, `Expected to be paired with ${anchor.code}`),
        ],
      },
    ];
  });

  const sharedPartners = [
    ...new Map(anchors.flatMap(movablePartnersOf).map((partner) => [partner.code, partner])).values(),
  ];
  const shared = sharedPartners.flatMap((partner): PairedCodeIssue[] => {
    const claimants = claimantsOf(partner.code);
    if (claimants.length < 2) return [];
    if (claimants.some((claimant) => isDirectlyAfter(claimant.occurrences, partner.occurrences))) return [];

    const message = `${partner.code} is expected to be paired with both ${joinAnd(claimants.map(({ code }) => code))}`;
    return [
      {
        kind: "shared-partner",
        reviewText: `${message}, but can only sit directly after one of them.`,
        offendingCodes: flagged(
          [...claimants.flatMap(({ occurrences }) => occurrences), ...partner.occurrences],
          message,
        ),
      },
    ];
  });

  return [...missing, ...several, ...separated, ...shared];
};

/** The per-code form of {@link findPairedCodeIssues}, for a UI that marks individual rows. */
export const pairedCodeWarnings = (
  orderedCodes: OrderedIcd10Codes,
  specialty: string | null | undefined,
): PairedCodeWarning[] => {
  const byIndex = findPairedCodeIssues(orderedCodes, specialty)
    .flatMap(({ offendingCodes }) => offendingCodes)
    .reduce(
      (warnings, { code, index, message }) =>
        warnings.set(index, { code, messages: new Set([...(warnings.get(index)?.messages ?? []), message]) }),
      new Map<number, { code: CanonicalIcd10Code; messages: Set<string> }>(),
    );

  return [...byIndex]
    .map(([index, { code, messages }]) => ({ code, index, message: [...messages].join("; ") }))
    .sort((left, right) => left.index - right.index);
};

const moveAfter = <T>(items: readonly T[], from: number, anchorIndex: number): T[] => {
  const withoutMoved = items.filter((_, index) => index !== from);
  const insertAt = anchorIndex - (from < anchorIndex ? 1 : 0) + 1;

  return [...withoutMoved.slice(0, insertAt), items[from], ...withoutMoved.slice(insertAt)];
};

/** Only a one-to-one pair is moved; a code that appears twice, or a partner another anchor also wants, stays put. */
const planMove = <T extends { code: string }>(
  items: readonly T[],
  anchorItem: T,
  specialty: string | null | undefined,
) => {
  const anchorIndex = items.indexOf(anchorItem);
  const { anchors, movablePartnersOf, claimantsOf } = analyse(
    items.map((item) => parseCanonicalIcd10Code(item.code)),
    specialty,
  );

  const anchor = anchors.find(({ occurrences }) => occurrences.some(({ index }) => index === anchorIndex));
  if (!anchor || anchor.occurrences.length !== 1) return undefined;

  const partners = movablePartnersOf(anchor);
  if (partners.length !== 1 || partners[0].occurrences.length !== 1) return undefined;

  const [partner] = partners[0].occurrences;
  if (claimantsOf(partner.code).length !== 1 || partner.index === anchorIndex + 1) return undefined;

  return { from: partner.index, anchorIndex };
};

/** Moves each partner to directly after its anchor. The anchor and every other code keep their order. */
export const placePairedCodes = <T extends { code: string }>(
  items: readonly T[],
  specialty: string | null | undefined,
): PairedCodePlacement<T> =>
  items.reduce<PairedCodePlacement<T>>(
    (state, anchorItem) => {
      const move = planMove(state.codesInSequence, anchorItem, specialty);
      if (!move) return state;

      return {
        codesInSequence: moveAfter(state.codesInSequence, move.from, move.anchorIndex),
        placed: [...state.placed, { partner: state.codesInSequence[move.from], anchor: anchorItem }],
      };
    },
    { codesInSequence: [...items], placed: [] },
  );
